import React, { useCallback, useEffect, useState } from "react";
import { Button, Calendar, Dropdown, ModalDialog } from "@culturehq/components";

import "../analytics/styles.scss";
import dateRange, { months } from "app/utils/dateRange";
import { Trans } from "app/utils";
import ChqModalDialog from "components/common/ChqModalDialog";

const options = [
  { value: "all", label: "All Time" },
  { value: "lst-30", label: "Last 30 Days" },
  { value: "lst-60", label: "Last 60 Days" },
  { value: "lst-90", label: "Last 90 Days" },
  { value: "year-to-date", label: "Year-to-date" },
  { value: "lst-year", label: "Last Year" }
];

const addZero = number => (number < 10 ? `0${number}` : number);

const CustomDateRange = ({ setDropdownSelection, setStartDateFilter,
  setEndDateFilter, dropdownSelection, onChange }) => {
  const [startDate, setStartDate] = useState({ year: null, month: null, day: null });
  const [endDate, setEndDate] = useState({ year: null, month: null, day: null });
  const [open, setOpen] = useState(false);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const onClose = useCallback(
    () => {
      setOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOpen]
  );

  useEffect(() => {
    if (dropdownSelection.value !== "custom") {
      setStartDate({ year: null, month: null, day: null });
      setEndDate({ year: null, month: null, day: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownSelection]);

  const applyDateRange = () => {
    if (!startDate.day || !endDate.day) return;

    const nextEndDate = new Date(endDate.year, endDate.month, endDate.day);
    nextEndDate.setDate(nextEndDate.getDate() + 1);

    setStartDateFilter(startDate);

    setEndDateFilter({
      year: nextEndDate.getFullYear(),
      month: nextEndDate.getMonth(),
      day: nextEndDate.getDate() });
    setOpen(false);

    const newSelection = { value: "custom", label: currentLabel() };
    setDropdownSelection(newSelection);
    onChange("rangeType", "custom");
  };

  const currentLabel = () => {
    const { start_date, end_date } = dateRange(startDate, endDate);
    if (start_date.year === end_date.year) {
      return `${months[start_date.month]} ${start_date.day} - ${months[end_date.month]} ${end_date.day}, ${start_date.year}`;
    }

    return `${months[start_date.month]} ${start_date.day}, ${start_date.year}  - ${months[end_date.month]} ${end_date.day}, ${end_date.year}`;
  };

  const onCalendarChange = (year, month, day, isRange) => {
    if (startDate.day === null) {
      setStartDate({ year, month, day });
    } else if (isRange) {
      if (endDate.day === null) {
        setEndDate({ year, month, day });
      } else {
        setEndDate({ year: null, month: null, day: null });
        setStartDate({ year, month, day });
      }
    } else {
      // regular calendar
      setStartDate({ year, month, day });
    }
  };

  return (
    <>
      <div onClick={onOpen}>
        <Dropdown.Option key="custom" value="custom">
          <Trans>Custom</Trans>
        </Dropdown.Option>
      </div>
      {open && (
        <ChqModalDialog onClose={onClose} className="select-range">
          <ModalDialog.Heading onClose={onClose}>
            <Trans>Select Date Range</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <Calendar
              disableFuture
              year={startDate.year}
              month={startDate.month}
              day={startDate.day}
              year2={endDate.year}
              month2={endDate.month}
              day2={endDate.day}
              onChange={onCalendarChange}
              range
            />
          </ModalDialog.Body>
          <ModalDialog.Footer>
            <div>
              <Button inverted onClick={onClose}>
                <Trans>Close</Trans>
              </Button>
            </div>
            <Button primary onClick={applyDateRange}>
              <Trans>Apply</Trans>
            </Button>
          </ModalDialog.Footer>
        </ChqModalDialog>
      )}
    </>
  );
};

const DateRangeFilter = ({ onChange }) => {
  const [dropdownSelection, setDropdownSelection] = useState(options[0]);
  const [startDate, setStartDate] = useState({ year: null, month: null, day: null });
  const [endDate, setEndDate] = useState({ year: null, month: null, day: null });

  useEffect(() => {
    if (!startDate.day || !endDate.day) {
      onChange("startDate", null);
      onChange("endDate", null);
      onChange("rangeType", dropdownSelection.value);
      return;
    }

    const { start_date, end_date } = dateRange(startDate, endDate);

    onChange("startDate", `${start_date.year}-${addZero(start_date.month + 1)}-${addZero(start_date.day)}`);
    onChange("endDate", `${end_date.year}-${addZero(end_date.month + 1)}-${addZero(end_date.day)}`);
    onChange("rangeType", dropdownSelection.value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, dropdownSelection]);

  const setDateRange = days => {
    const today = new Date(new Date().setDate(new Date().getDate() + 1));

    const otherDay = new Date(new Date().setDate(new Date().getDate() - days));
    setEndDate({ year: today.getFullYear(), month: today.getMonth(), day: today.getDate() });
    setStartDate({
      year: otherDay.getFullYear(),
      month: otherDay.getMonth(),
      day: otherDay.getDate() });
  };

  const yearToDate = () => {
    const today = new Date(new Date().setDate(new Date().getDate() + 1));
    const otherDay = new Date(today.getFullYear(), 0, 1);
    setEndDate({ year: today.getFullYear(), month: today.getMonth(), day: today.getDate() });
    setStartDate({
      year: otherDay.getFullYear(),
      month: otherDay.getMonth(),
      day: otherDay.getDate() });
  };

  const onDateRangeSelection = value => {
    switch (value) {
      case "lst-30":
        setDateRange(30);
        setDropdownSelection(options.find(option => option.value === "lst-30"));
        break;
      case "lst-60":
        setDateRange(60);
        setDropdownSelection(options.find(option => option.value === "lst-60"));
        break;
      case "lst-90":
        setDateRange(90);
        setDropdownSelection(options.find(option => option.value === "lst-90"));
        break;
      case "year-to-date":
        yearToDate();
        setDropdownSelection(options.find(option => option.value === "year-to-date"));
        break;
      case "lst-year":
        setDateRange(365);
        setDropdownSelection(options.find(option => option.value === "lst-year"));
        break;
      case "all":
        setDropdownSelection(options.find(option => option.value === "all"));
        setStartDate({ year: null, month: null, day: null });
        setEndDate({ year: null, month: null, day: null });
        break;
      default:
    }
  };

  return (
    <Dropdown selected={dropdownSelection.value} onChange={value => onDateRangeSelection(value)}>
      <Dropdown.Button className="chq-btn Gray-Button">
        {dropdownSelection.label}
      </Dropdown.Button>
      <Dropdown.ListBox className="custom_option dp-ehg">
        {options.map(option => (
          <Dropdown.Option key={option.value} value={option.value}>
            <Trans>{option.label}</Trans>
          </Dropdown.Option>
        ))}
        <CustomDateRange
          setDropdownSelection={setDropdownSelection}
          setStartDateFilter={setStartDate}
          setEndDateFilter={setEndDate}
          dropdownSelection={dropdownSelection}
          onChange={onChange}
        />
      </Dropdown.ListBox>
    </Dropdown>
  );
};

export default DateRangeFilter;
